/**
 * Quantity, freight and montering for an order of one or more cart lines —
 * the cart and checkout half of P25009-474 (the listing page half is
 * listingOrderSummary.js, which this module builds on).
 *
 * Rules, each the same one the listing page and the server apply:
 *   limits    a per-item line runs 1..stock; a set line is locked at one set
 *             (BR-3 / BR-4, quantityBounds)
 *   freight   every line pays its own per-piece freight × its own quantity,
 *             summed (BR-5, server/api-util/lineItems.js); pickup is free
 *   services  the selected ones that apply to the order, each by its own
 *             price unit (P25009-484, serviceTargetingCore.js)
 *   miljöavgift 25 kr per tyre on new tyres and new complete wheels
 *             (P25009-484 BR-6)
 *
 * Quantity is always in SALES units (a set line sells sets); wheel counts come
 * from physicalItemCount, as everywhere else.
 */
import { getUnitNoun, resolveSalesUnit } from './salesUnit';
import { physicalItemCount } from './freightCore';
import {
  miljoavgiftTyres,
  MILJOAVGIFT_PER_TYRE_SUBUNITS,
  servicesForOrder,
  servicesOnOrder,
  serviceSubunits,
} from './serviceTargetingCore';
import { quantityBounds, shippingSubunitsFor } from './listingOrderSummary';

const trackedStock = stock => (typeof stock === 'number' && Number.isFinite(stock) ? stock : null);

/**
 * What one line's quantity may be set to, plus what the label needs. Stored on
 * a cart line so the mini cart can render a stepper without a network call.
 *
 * @returns {{ min: number, max: number, locked: boolean, setSize: number,
 *   noun: { one: string, many: string } }}
 */
export const lineLimits = (publicData = {}, stock) => {
  const { min, max, locked } = quantityBounds(publicData, trackedStock(stock));
  const { setSize } = resolveSalesUnit(publicData);
  return { min, max, locked, setSize, noun: getUnitNoun(publicData) };
};

/** `quantity` kept inside `limits`; without limits, at least 1. */
export const clampQuantity = (quantity, limits) => {
  const n = Number.parseInt(quantity, 10);
  const qty = Number.isFinite(n) ? n : 1;
  if (!limits) return Math.max(1, qty);
  if (limits.locked) return 1;
  return Math.max(limits.min || 1, Math.min(limits.max, qty));
};

/**
 * What a line covers, in words: "4 däck", "1 fälg", "1 sats · 4 hjul".
 * Without limits (a line stored before they existed) the bare count.
 */
export const quantityLabel = (limits, quantity) => {
  if (!limits?.noun) return `${quantity} st`;
  const { noun, locked, setSize } = limits;
  if (locked) return `1 sats · ${setSize} ${setSize === 1 ? noun.one : noun.many}`;
  return `${quantity} ${quantity === 1 ? noun.one : noun.many}`;
};

/** Physical wheels in one line. */
export const lineWheels = (publicData = {}, quantity) =>
  physicalItemCount({ quantity, ...resolveSalesUnit(publicData) });

/** The order's lines as serviceTargetingCore reads them. */
export const serviceLinesOf = lines =>
  (Array.isArray(lines) ? lines : []).filter(Boolean).map(l => ({
    publicData: l.publicData || {},
    metadata: l.metadata || {},
    physicalItems: lineWheels(l.publicData || {}, l.quantity),
  }));

/**
 * The seller's services on this order, as the server will charge them: the
 * ids the buyer chose that apply, plus montering locked onto new complete
 * wheels (P25009-484 BR-5). Paused services are never on an order.
 */
export const selectedServicesOf = (services, selectedServiceIds, lines) =>
  servicesOnOrder(services, selectedServiceIds, serviceLinesOf(lines));

/**
 * Freight, services and miljöavgift for a whole order, in subunits — what the
 * server will charge, worked out from the lines alone. The freight matrix
 * (FREIGHT_MATRIX_CHECKOUT_ENABLED) is a server flag the browser cannot see,
 * so where a priced order exists its own lines are the truth.
 *
 * @param {Object} params
 * @param {Array}  params.lines  [{ publicData, quantity }] — the primary first
 * @param {string} params.deliveryMethod 'shipping' | 'pickup' | anything else
 * @param {Array} [params.services] the services the buyer has on the order
 *   (selectedServicesOf); only those that apply to a line are priced
 * @returns {{ wheels: number, freightSubunits: number|null,
 *   services: Array<{ service, subunits }>, servicesSubunits: number,
 *   miljoavgiftTyres: number, miljoavgiftSubunits: number }}
 *   freightSubunits is null when no delivery method is chosen yet
 */
export const orderExtras = ({ lines, deliveryMethod, services = [] }) => {
  const list = (Array.isArray(lines) ? lines : []).filter(Boolean);
  const serviceLines = serviceLinesOf(list);
  const priced = servicesForOrder(services, serviceLines)
    .map(service => ({ service, subunits: serviceSubunits(service, serviceLines) }))
    .filter(row => row.subunits > 0);
  const wheels = list.reduce((sum, l) => sum + lineWheels(l.publicData, l.quantity), 0);
  const freightSubunits =
    deliveryMethod === 'shipping'
      ? list.reduce((sum, l) => sum + shippingSubunitsFor(l.publicData || {}, l.quantity), 0)
      : deliveryMethod === 'pickup'
      ? 0
      : null;
  return {
    wheels,
    freightSubunits,
    services: priced,
    servicesSubunits: priced.reduce((sum, row) => sum + row.subunits, 0),
    miljoavgiftTyres: miljoavgiftTyres(serviceLines),
    miljoavgiftSubunits: miljoavgiftTyres(serviceLines) * MILJOAVGIFT_PER_TYRE_SUBUNITS,
  };
};
